import React from 'react';
import { Button, Input } from '@/shared/components/ui';
import { getRequiredLabel } from '@/shared/lib';
import { useTranslation } from 'react-i18next';
import CalendarIcon from '@/shared/assets/calendar-icon.svg?react';
import { DEFAULT_DATE_PICKER_INPUT_YEAR } from '@/shared/config';

interface Props extends React.InputHTMLAttributes<HTMLInputElement> {
  handleDateModalOpen: () => void;
}

const DATE_MASK_PLACEHOLDER = '__.__.____';

function formatDate(value: string) {
  const digits = value.replace(/\D/g, '').slice(0, 8);

  const day = digits.slice(0, 2);
  const month = digits.slice(2, 4);
  const year = digits.slice(4, 8);

  let result = day;

  if (month) result += `.${month}`;
  if (year) {
    if (year.length === 4) {
      const numericYear = Number(year);

      const safeYear =
        numericYear < DEFAULT_DATE_PICKER_INPUT_YEAR
          ? DEFAULT_DATE_PICKER_INPUT_YEAR
          : numericYear;

      result += `.${safeYear}`;
    } else {
      result += `.${year}`;
    }
  }

  return result;
}

function expandTwoDigitYear(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (digits.length !== 6) return value;

  const day = digits.slice(0, 2);
  const month = digits.slice(2, 4);
  const yy = digits.slice(4, 6);
  const yyNum = Number(yy);
  const century = yyNum <= 26 ? '20' : '19';
  const fullYear = `${century}${yy}`;

  return `${day}.${month}.${fullYear}`;
}

const DatePickerInput = React.forwardRef<HTMLInputElement, Props>(
  ({ handleDateModalOpen, onChange, onBlur, value, ...props }, ref) => {
    const { t } = useTranslation();
    const inputRef = React.useRef<HTMLInputElement>(null);
    const [isFocused, setIsFocused] = React.useState(false);

    const stringValue = typeof value === 'string' ? value : '';

    const showMaskPlaceholder = isFocused;

    const measureRef = React.useRef<HTMLSpanElement>(null);
    const [textWidth, setTextWidth] = React.useState(0);

    React.useLayoutEffect(() => {
      if (measureRef.current) {
        setTextWidth(measureRef.current.offsetWidth);
      } else {
        setTextWidth(0);
      }
    }, [stringValue, isFocused]);

    React.useEffect(() => {
      const checkFocus = () => {
        if (inputRef.current) {
          setIsFocused(document.activeElement === inputRef.current);
        }
      };

      checkFocus();

      const handleFocusIn = () => setTimeout(checkFocus, 0);
      const handleFocusOut = () => setTimeout(checkFocus, 0);

      document.addEventListener('focusin', handleFocusIn);
      document.addEventListener('focusout', handleFocusOut);

      return () => {
        document.removeEventListener('focusin', handleFocusIn);
        document.removeEventListener('focusout', handleFocusOut);
      };
    }, []);

    const handleRef = React.useCallback(
      (node: HTMLInputElement | null) => {
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref && node) {
          (ref as React.MutableRefObject<HTMLInputElement | null>).current =
            node;
        }
        if (node) {
          inputRef.current = node;
        }
      },
      [ref]
    );

    const handleChange = React.useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const input = e.target;
        const rawValue = input.value;
        const caretPos = input.selectionStart ?? rawValue.length;

        const digitsBeforeCaret = rawValue
          .slice(0, caretPos)
          .replace(/\D/g, '').length;

        const formatted = formatDate(rawValue);

        let newCaret = formatted.length;
        if (digitsBeforeCaret === 0) {
          newCaret = 0;
        } else {
          let digitCount = 0;
          for (let i = 0; i < formatted.length; i++) {
            if (/\d/.test(formatted[i] ?? '')) digitCount++;
            if (digitCount === digitsBeforeCaret) {
              newCaret = i + 1;
              break;
            }
          }
        }

        const syntheticEvent = {
          target: { value: formatted },
          currentTarget: { value: formatted },
        } as React.ChangeEvent<HTMLInputElement>;

        onChange?.(syntheticEvent);

        requestAnimationFrame(() => {
          if (inputRef.current) {
            try {
              inputRef.current.setSelectionRange(newCaret, newCaret);
            } catch {
              // ignore if not focusable
            }
          }
        });
      },
      [onChange]
    );

    const handleBlur = React.useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        const currentVal = (e.target.value as string) ?? stringValue;
        const digits = currentVal.replace(/\D/g, '');

        if (digits.length === 6) {
          const expanded = expandTwoDigitYear(currentVal);
          if (expanded !== currentVal) {
            const syntheticEvent = {
              target: { value: expanded },
              currentTarget: { value: expanded },
            } as unknown as React.ChangeEvent<HTMLInputElement>;
            onChange?.(syntheticEvent);
          }
        }

        onBlur?.(e);
      },
      [onBlur, onChange, stringValue]
    );

    return (
      <div className="relative w-full">
        <Input
          {...props}
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          onBeforeInput={e => {
            if (!/\d/.test(e.data || '')) {
              e.preventDefault();
            }
          }}
          ref={handleRef}
          type="text"
          inputMode="numeric"
          maxLength={10}
          pattern="\d{2}\.\d{2}\.\d{4}"
          placeholder=""
          label={getRequiredLabel(
            t('personalAccount.verification.eulaProfileForm.dateOfBirthLabel')
          )}
          className="p3"
          rightContent={
            <Button
              disabled={props.disabled}
              type="button"
              size="icon-button"
              variant="icon"
              onClick={handleDateModalOpen}>
              <CalendarIcon className="size-[2.4rem]" />
            </Button>
          }
        />

        {/* hidden measurer for pixel-perfect mask positioning */}
        <span
          ref={measureRef}
          className="p3 invisible absolute whitespace-pre left-[1.6rem] top-0 pointer-events-none"
          aria-hidden="true">
          {stringValue}
        </span>

        {showMaskPlaceholder &&
          (!stringValue ? (
            <div
              className="absolute top-1/2 -translate-y-1/2 pointer-events-none p3 pt-[0.8rem] left-[1.6rem] tracking-[0.06em] whitespace-pre"
              aria-hidden="true">
              <span className="text-muted-foreground/50">
                {DATE_MASK_PLACEHOLDER}
              </span>
            </div>
          ) : DATE_MASK_PLACEHOLDER.slice(stringValue.length) ? (
            <div
              className="absolute top-1/2 -translate-y-1/2 pointer-events-none p3 pt-[0.8rem] whitespace-pre tracking-[0.06em]"
              style={{
                left: `calc(1.6rem + ${textWidth}px + 0.16rem)`,
              }}
              aria-hidden="true">
              <span className="text-muted-foreground/50">
                {DATE_MASK_PLACEHOLDER.slice(stringValue.length)}
              </span>
            </div>
          ) : null)}
      </div>
    );
  }
);

DatePickerInput.displayName = 'DatePickerInput';

export { DatePickerInput };
